import React, { useMemo } from 'react';
import { Grid } from '@mui/material';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import SouthWestOutlinedIcon from '@mui/icons-material/SouthWestOutlined';
import NorthEastOutlinedIcon from '@mui/icons-material/NorthEastOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';
import { FP, StatCard } from 'components/financepro';
import EstadoFinancieroShell, {
  fmtMoney,
  fmtMontoContable,
  textoPeriodoRango,
} from './EstadoFinancieroShell';
import PlantillaEstadoFinanciero, { LAYOUT_BALANZA } from './PlantillaEstadoFinanciero';
import { buildPlantillaEstadoFinancieroPrint } from './plantillaEstadoFinancieroPrint';

/** Clave del programa para validar permisos (mismo patrón que FacturaPOS). */
export const PROGRAMA_BALANZA_DE_COMPROBACION = 'balanzadecomprobacion';

const ORDEN_GRUPOS = ['Activo', 'Pasivo', 'Capital', 'Ingresos', 'Costos', 'Gastos'];

const GRUPO_POR_DIGITO = {
  1: 'Activo',
  2: 'Pasivo',
  3: 'Capital',
  4: 'Ingresos',
  5: 'Costos',
  6: 'Gastos',
};

const COLUMNAS = [
  { id: 'cuenta', label: 'Cuenta', tipo: 'cuenta', ocultarEnMovil: true },
  { id: 'nombre', label: 'Nombre', tipo: 'texto' },
  { id: 'debe', label: 'Debe', tipo: 'monto' },
  { id: 'haber', label: 'Haber', tipo: 'monto' },
  { id: 'saldo_deudor', label: 'Saldo deudor', tipo: 'monto', vacioSiCero: true },
  { id: 'saldo_acreedor', label: 'Saldo acreedor', tipo: 'monto', vacioSiCero: true },
];

const redondear = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;

const grupoDe = (row) => {
  const g = String(row.grupo || '').trim();
  if (g) return g;
  const d = String(row.cuenta || '').trim().charAt(0);
  return GRUPO_POR_DIGITO[d] || 'Otras cuentas';
};

const etiquetaSeccion = (titulo) => {
  const t = String(titulo || '').toLowerCase();
  if (t.includes('activo')) return 'Bienes y derechos de la empresa';
  if (t.includes('pasivo')) return 'Obligaciones con terceros';
  if (t.includes('capital') || t.includes('patrimonio')) return 'Patrimonio de los propietarios';
  if (t.includes('ingreso')) return 'Ingresos del período';
  if (t.includes('costo')) return 'Costo de ventas y costos operativos';
  if (t.includes('gasto')) return 'Gastos de administración, ventas y financieros';
  return '';
};

const agruparBalanza = (filas) => {
  const cuentas = (filas || []).filter((r) => !r.es_titulo && !r.es_total);
  const porGrupo = new Map();

  cuentas.forEach((row) => {
    const g = grupoDe(row);
    if (!porGrupo.has(g)) {
      porGrupo.set(g, {
        titulo: g,
        cuentas: [],
        debe: 0,
        haber: 0,
        saldoDeudor: 0,
        saldoAcreedor: 0,
      });
    }
    const sec = porGrupo.get(g);
    sec.cuentas.push(row);
    sec.debe = redondear(sec.debe + (parseFloat(row.debe) || 0));
    sec.haber = redondear(sec.haber + (parseFloat(row.haber) || 0));
    sec.saldoDeudor = redondear(sec.saldoDeudor + (parseFloat(row.saldo_deudor) || 0));
    sec.saldoAcreedor = redondear(sec.saldoAcreedor + (parseFloat(row.saldo_acreedor) || 0));
  });

  const secciones = [];
  ORDEN_GRUPOS.forEach((g) => {
    if (porGrupo.has(g)) {
      secciones.push(porGrupo.get(g));
      porGrupo.delete(g);
    }
  });
  porGrupo.forEach((sec) => secciones.push(sec));

  const sumar = (campo) => redondear(secciones.reduce((s, x) => s + x[campo], 0));
  const debe = sumar('debe');
  const haber = sumar('haber');
  const saldoDeudor = sumar('saldoDeudor');
  const saldoAcreedor = sumar('saldoAcreedor');
  const difSumas = redondear(debe - haber);
  const difSaldos = redondear(saldoDeudor - saldoAcreedor);
  const cuadra = Math.abs(difSumas) < 0.005 && Math.abs(difSaldos) < 0.005;

  return {
    secciones,
    cuentas,
    debe,
    haber,
    saldoDeudor,
    saldoAcreedor,
    difSumas,
    difSaldos,
    cuadra,
  };
};

const celdasMontos = (row) => ({
  debe: row.debe,
  haber: row.haber,
  saldo_deudor: row.saldoDeudor ?? row.saldo_deudor,
  saldo_acreedor: row.saldoAcreedor ?? row.saldo_acreedor,
});

export const construirModeloBalanza = ({ filas, fechaDesde, fechaHasta, nombreEmpresa }) => {
  const { secciones, debe, haber, saldoDeudor, saldoAcreedor, cuadra } = agruparBalanza(filas);

  return {
    nombreEmpresa,
    tituloDocumento: 'Balanza de comprobación',
    periodo: textoPeriodoRango(fechaDesde, fechaHasta),
    columnas: COLUMNAS,
    layout: {
      ...LAYOUT_BALANZA,
      grid: {
        ...LAYOUT_BALANZA.grid,
        print: '90px 1.5fr repeat(4, minmax(72px, 1fr))',
      },
    },
    mensajeVacio: 'Sin movimientos en esta sección',
    secciones: secciones.map((s) => ({
      titulo: s.titulo,
      subtitulo: etiquetaSeccion(s.titulo),
      filas: s.cuentas.map((c) => ({
        cuenta: c.cuenta,
        nombre: c.nombre,
        celdas: {
          debe: c.debe,
          haber: c.haber,
          saldo_deudor: c.saldo_deudor,
          saldo_acreedor: c.saldo_acreedor,
        },
      })),
      total: {
        etiqueta: `Total ${s.titulo}`,
        celdas: celdasMontos(s),
      },
    })),
    totales: [
      {
        etiqueta: cuadra ? 'Totales · Cuadra' : 'Totales · Descuadre',
        variante: 'neto',
        tono: cuadra ? 'ok' : 'alerta',
        celdas: {
          debe,
          haber,
          saldo_deudor: saldoDeudor,
          saldo_acreedor: saldoAcreedor,
        },
      },
    ],
    nota: 'Sumas Debe = Haber · Saldos deudores = Saldos acreedores · Los importes negativos se muestran entre paréntesis',
    firmas: [{ etiqueta: 'Preparado por' }, { etiqueta: 'Revisado por' }],
  };
};

const CuerpoBalanza = (ctx) => {
  const modelo = useMemo(
    () => construirModeloBalanza(ctx),
    [ctx.filas, ctx.fechaDesde, ctx.fechaHasta, ctx.nombreEmpresa]
  );
  return <PlantillaEstadoFinanciero {...modelo} />;
};

const ResumenBalanza = ({ filas }) => {
  const { debe, haber, saldoDeudor, saldoAcreedor, cuadra } = agruparBalanza(filas);

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label="Total debe"
          value={`$${fmtMoney(debe)}`}
          icon={SouthWestOutlinedIcon}
          accent={FP.color.accentBlue}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label="Total haber"
          value={`$${fmtMoney(haber)}`}
          icon={NorthEastOutlinedIcon}
          accent={FP.color.warning}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label="Saldo deudor"
          value={`$${fmtMoney(saldoDeudor)}`}
          icon={AccountBalanceWalletOutlinedIcon}
          accent={FP.color.navyMuted}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          label={cuadra ? 'Balanza cuadrada' : 'Descuadre'}
          value={`$${fmtMoney(saldoAcreedor)}`}
          icon={BalanceOutlinedIcon}
          accent={cuadra ? FP.color.success : FP.color.danger}
        />
      </Grid>
    </Grid>
  );
};

const columns = [
  { id: 'cuenta', label: 'Cuenta', align: 'left', width: '14%' },
  { id: 'nombre', label: 'Nombre', align: 'left', width: '26%' },
  {
    id: 'debe',
    label: 'Debe',
    align: 'right',
    width: '15%',
    render: (v) => `$${fmtMontoContable(v)}`,
  },
  {
    id: 'haber',
    label: 'Haber',
    align: 'right',
    width: '15%',
    render: (v) => `$${fmtMontoContable(v)}`,
  },
  {
    id: 'saldo_deudor',
    label: 'Saldo deudor',
    align: 'right',
    width: '15%',
    render: (v) => `$${fmtMontoContable(v)}`,
  },
  {
    id: 'saldo_acreedor',
    label: 'Saldo acreedor',
    align: 'right',
    width: '15%',
    render: (v) => `$${fmtMontoContable(v)}`,
  },
];

const BalanzaDeComprobacion = () => (
  <EstadoFinancieroShell
    programa={PROGRAMA_BALANZA_DE_COMPROBACION}
    title="Balanza de Comprobación"
    subtitle="Sumas y saldos del período"
    reportTitle="Balanza de Comprobación"
    IconComponent={TableChartOutlinedIcon}
    endpoint="contabilidadbackend_balanzadecomprobacion"
    columns={columns}
    emptyMessage="No hay cuentas con movimiento para armar la balanza"
    mapResponseRows={(data) => data.lineas || data.filas || data.cuentas || []}
    hideFooter
    footerFromRows={(filas) => {
      const { debe, haber, cuadra } = agruparBalanza(filas);
      if (!filas.length) return '';
      return `Total debe: $${fmtMontoContable(debe)} · Total haber: $${fmtMontoContable(haber)}${
        cuadra ? '' : ' · Descuadre'
      }`;
    }}
    renderSummary={(ctx) => <ResumenBalanza filas={ctx.filas} />}
    renderBody={(ctx) => <CuerpoBalanza {...ctx} />}
    buildPrintHtml={(ctx) => buildPlantillaEstadoFinancieroPrint(construirModeloBalanza(ctx))}
  />
);

export default BalanzaDeComprobacion;
